<template>
  <div>
    <AdminHeader />

    <!-- Access denied -->
    <div v-if="loggedInUser.is_super != 1" class="state-block">
      <div class="state-icon state-icon--red">
        <font-awesome-icon icon="lock" />
      </div>
      <p class="state-title">Access Restricted</p>
      <p class="state-sub">Only super administrators can view company information.</p>
    </div>

    <!-- Loading -->
    <div v-else-if="loading" class="state-block">
      <div class="spinner-border text-primary" style="width:3rem;height:3rem;" role="status"></div>
      <p class="mt-3 text-muted-cell">Loading company info...</p>
    </div>

    <!-- Content -->
    <div v-else class="company-card">

      <!-- Card header -->
      <div class="card-header-row">
        <div>
          <h5 class="card-title">General Company Info</h5>
          <p class="card-sub">Your company profile and contact details.</p>
        </div>
        <router-link v-if="permission.update_company == 1" to="edit-company-info" class="btn-edit">
          <font-awesome-icon icon="edit" /> Edit Profile
        </router-link>
      </div>

      <div class="card-divider"></div>

      <!-- Logo + company name -->
      <div class="logo-section">
        <div class="logo-wrap">
          <img
            v-if="companyInfo.logo"
            :src="host.hostName + companyInfo.logo"
            alt="Company Logo"
            class="company-logo"
          />
          <div v-else class="logo-placeholder">
            <font-awesome-icon icon="building" />
          </div>
        </div>
        <div class="company-name-wrap">
          <div class="company-name">{{ companyInfo.company_name ?? '—' }} - ({{ companyInfo.code }})</div>
          <div class="company-type" v-if="companyInfo.business_type">{{ companyInfo.business_type }}</div>
        </div>
      </div>

      <div class="card-divider"></div>

      <!-- Info grid -->
      <div class="info-grid">

        <div class="info-section">
          <div class="section-title">Contact</div>
          <div class="fields-row">
            <div class="field-item">
              <div class="field-label">
                <font-awesome-icon icon="envelope" class="field-icon" /> Email
              </div>
              <div class="field-value">{{ companyInfo.company_email ?? '—' }}</div>
            </div>
            <div class="field-item">
              <div class="field-label">
                <font-awesome-icon icon="phone" class="field-icon" /> Phone Number
              </div>
              <div class="field-value">{{ companyInfo.phone ?? '—' }}</div>
            </div>
            <div class="field-item">
              <div class="field-label">
                <font-awesome-icon icon="map-marker-alt" class="field-icon" /> Address
              </div>
              <div class="field-value">{{ companyInfo.address ?? '—' }}</div>
            </div>
          </div>
        </div>

        <div class="card-divider"></div>

        <div class="info-section">
          <div class="section-title">Location</div>
          <div class="fields-row">
            <div class="field-item">
              <div class="field-label">
                <font-awesome-icon icon="globe" class="field-icon" /> Country
              </div>
              <div class="field-value">{{ companyInfo.country?.name ?? '—' }}</div>
            </div>
            <div class="field-item">
              <div class="field-label">
                <font-awesome-icon icon="map" class="field-icon" /> State
              </div>
              <div class="field-value">{{ companyInfo.state?.name ?? '—' }}</div>
            </div>
            <div class="field-item">
              <div class="field-label">
                <font-awesome-icon icon="city" class="field-icon" /> City
              </div>
              <div class="field-value">{{ companyInfo.city?.name ?? '—' }}</div>
            </div>
            <div class="field-item">
              <div class="field-label">
                <font-awesome-icon icon="mail-bulk" class="field-icon" /> Postal Code
              </div>
              <div class="field-value">{{ companyInfo.postal_code ?? '—' }}</div>
            </div>
          </div>
        </div>

        <div class="card-divider"></div>

        <div class="info-section">
          <div class="section-title">Registration</div>
          <div class="fields-row">
            <div class="field-item">
              <div class="field-label">
                <font-awesome-icon icon="id-card" class="field-icon" /> RC Number
              </div>
              <div class="field-value">{{ companyInfo.rc_number ?? '—' }}</div>
            </div>
            <!-- <div class="field-item">
              <div class="field-label">
                <font-awesome-icon icon="coins" class="field-icon" /> Currency
              </div>
              <div class="field-value">
                <span v-if="companyInfo.currency?.name" class="currency-tag">
                  {{ companyInfo.currency.name }}
                </span>
                <span v-else>—</span>
              </div>
            </div> -->
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import axios from 'axios';
import AdminHeader from "./administrationheadertab.vue";
import host from '../../../main';

export default {
  name: 'CompanyInfo',

  components: { AdminHeader },

  setup() {
    const store       = useStore();
    const companyInfo = ref({});
    const loading     = ref(false);

    const loggedInUser = computed(() => store.state.getLoggedInUser);
    const permission   = computed(() => store.state.getCurrentUserPermission);

    const getCompanyInfo = async () => {
      loading.value = true;
      try {
        const { data } = await axios.get('settings/company');
        companyInfo.value = data?.data?.data ?? data?.data ?? {};
      } catch (err) {
        console.log(err.response?.data?.message);
      }
      finally { loading.value = false; }
    };

    onMounted(getCompanyInfo);

    return { companyInfo, loading, loggedInUser, permission, host };
  },
};
</script>

<style scoped>
/* ── States ──────────────────────────────────────────────────────────── */
.state-block { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:64px 24px; text-align:center; }
.state-icon  { width:60px; height:60px; border-radius:16px; display:flex; align-items:center; justify-content:center; font-size:24px; margin-bottom:16px; }
.state-icon--red { background:#fff0f0; color:#e03535; }
.state-title { font-size:16px; font-weight:700; color:#1a2b45; margin:0 0 6px; }
.state-sub   { font-size:13px; color:#7a90b0; margin:0; }
.text-muted-cell { color:#4a5e78; }
.mt-3 { margin-top:12px; }

/* ── Card ────────────────────────────────────────────────────────────── */
.company-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; margin-top:20px; }

/* ── Card header ─────────────────────────────────────────────────────── */
.card-header-row { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; padding:24px 28px; }
.card-title      { font-size:17px; font-weight:700; color:#1a2b45; margin:0 0 4px; }
.card-sub        { font-size:13px; color:#7a90b0; margin:0; }
.card-divider    { border:none; border-top:1px solid #f0f4f8; }

.btn-edit { display:inline-flex; align-items:center; gap:7px; background:#f4f7fb; color:#004AAD; border:1px solid #c5d6f5; border-radius:8px; padding:8px 16px; font-size:13px; font-weight:600; text-decoration:none; transition:all .15s; }
.btn-edit:hover { background:#e8f0fb; color:#003b8e; text-decoration:none; }

/* ── Logo section ────────────────────────────────────────────────────── */
.logo-section     { display:flex; align-items:center; gap:20px; padding:24px 28px; }
.logo-wrap        { flex-shrink:0; }
.company-logo     { width:80px; height:80px; object-fit:contain; border-radius:10px; border:1px solid #e4eaf2; background:#f8fafc; padding:4px; }
.logo-placeholder { width:80px; height:80px; border-radius:10px; border:1px solid #e4eaf2; background:#f4f7fb; display:flex; align-items:center; justify-content:center; font-size:28px; color:#7a90b0; }
.company-name     { font-size:20px; font-weight:700; color:#1a2b45; margin-bottom:4px; }
.company-type     { display:inline-block; background:rgba(0,74,173,.08); color:#004AAD; border:1px solid rgba(0,74,173,.18); border-radius:6px; padding:2px 10px; font-size:12px; font-weight:600; }

/* ── Info grid ───────────────────────────────────────────────────────── */
.info-grid    { display:flex; flex-direction:column; }
.info-section { padding:22px 28px; }
.section-title { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.06em; color:#7a90b0; margin-bottom:18px; }

.fields-row { display:grid; grid-template-columns:repeat(3, 1fr); gap:20px; }

.field-item  { display:flex; flex-direction:column; gap:6px; }
.field-label { font-size:11.5px; font-weight:600; color:#7a90b0; display:flex; align-items:center; gap:6px; text-transform:uppercase; letter-spacing:.04em; }
.field-icon  { font-size:11px; }
.field-value { font-size:14px; color:#1a2b45; font-weight:500; }

/* ── Currency tag ────────────────────────────────────────────────────── */
.currency-tag { display:inline-flex; align-items:center; background:#f4f7fb; color:#3d5068; border:1px solid #e4eaf2; border-radius:6px; padding:3px 10px; font-size:13px; font-weight:600; }

/* ── Responsive ──────────────────────────────────────────────────────── */
@media (max-width:768px) {
  .card-header-row { flex-direction:column; }
  .logo-section    { flex-direction:column; align-items:flex-start; }
  .fields-row      { grid-template-columns:1fr 1fr; }
}
@media (max-width:480px) {
  .fields-row { grid-template-columns:1fr; }
}
</style>